import React, { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import { PageHeader } from "../components/Section";
import { CONTACT_INFO } from "../data/site";

//const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INQUIRY_TYPES = [
  { value: "training", label: "Training & Certification" },
  { value: "operational_excellence", label: "Operational Excellence" },
  { value: "ai_agency", label: "AI Automation" },
  { value: "business_review", label: "Business Review" },
  { value: "business_turnaround", label: "Business Turnaround" },
  { value: "consulting", label: "Consulting (other)" },
  { value: "general", label: "General Enquiry" },
];

const Field = ({ label, name, required, children }) => (
  <label className="flex flex-col gap-2">
    <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
      {label} {required && <span className="text-[#E63946]">*</span>}
    </span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-white border border-black/15 px-4 py-3 text-sm text-black placeholder:text-zinc-400 focus:outline-none focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946] transition-colors";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    inquiry_type: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      toast.success("Message sent — we'll be in touch within one business day.");
      setForm({ name: "", email: "", company: "", phone: "", inquiry_type: "general", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join("; ")
        : detail || "Could not send. Please try again or email us directly.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Toaster position="top-center" richColors />
      <PageHeader
        overline="Contact"
        title={<>Let's <span className="text-[#E63946]">talk operations.</span></>}
        kicker="Tell us a little about your situation. Most engagements start with a 30-minute call — we'll listen, share what we've seen and tell you honestly whether we're the right team."
      />

      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.22em] uppercase text-[#E63946] font-semibold mb-6">
                Direct lines
              </p>
              <h2 className="font-display text-3xl lg:text-4xl font-black tracking-tighter leading-[1.05]">
                Hong Kong office.
              </h2>
              <p className="mt-6 text-zinc-600 leading-relaxed">
                We respond to enquiries within one business day. Urgent turnaround situations:
                please mark your message accordingly and we will route it straight to the
                partner on call.
              </p>

              <ul className="mt-12 space-y-7">
                <li className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
                      Email
                    </p>
                    <a
                      data-testid="contact-info-email"
                      href={`mailto:${CONTACT_INFO.email}`}
                      className="mt-1 inline-block text-base text-black hover:text-[#E63946] transition-colors"
                    >
                      {CONTACT_INFO.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
                      Phone
                    </p>
                    <p className="mt-1 text-base text-black" data-testid="contact-info-phone">
                      {CONTACT_INFO.phone}
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
                      Office
                    </p>
                    <p className="mt-1 text-base text-black">{CONTACT_INFO.address}</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Clock className="w-5 h-5 text-[#E63946] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">
                      Hours
                    </p>
                    <p className="mt-1 text-base text-black">{CONTACT_INFO.hours}</p>
                  </div>
                </li>
              </ul>

              <div className="mt-12 pt-8 border-t border-black/10">
                <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-semibold mb-4">
                  Delivery regions
                </p>
                <div className="flex flex-wrap gap-2">
                  {CONTACT_INFO.regions.map((r) => (
                    <span
                      key={r}
                      className="text-[11px] tracking-[0.18em] uppercase text-zinc-600 border border-black/15 px-2.5 py-1"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              {submitted ? (
                <div
                  data-testid="contact-success"
                  className="border border-black/10 bg-[#F7F7F8] p-12 lg:p-16 flex flex-col items-start"
                >
                  <CheckCircle2 className="w-10 h-10 text-[#E63946]" strokeWidth={1.5} />
                  <h3 className="mt-6 font-display text-3xl lg:text-4xl font-black tracking-tighter">
                    Message received.
                  </h3>
                  <p className="mt-4 text-zinc-700 leading-relaxed max-w-md">
                    Thanks for reaching out. A member of our team will reply within one business
                    day from {CONTACT_INFO.email}.
                  </p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 text-sm font-semibold text-black border-b border-black hover:text-[#E63946] hover:border-[#E63946] pb-1 transition-colors"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form
                  data-testid="contact-form"
                  onSubmit={onSubmit}
                  className="border border-black/10 bg-white p-8 lg:p-12 grid grid-cols-1 sm:grid-cols-2 gap-6"
                >
                  <div className="sm:col-span-2">
                    <Field label="Full name" name="name" required>
                      <input
                        data-testid="contact-input-name"
                        required
                        value={form.name}
                        onChange={update("name")}
                        className={inputCls}
                        placeholder="Jane Chan"
                      />
                    </Field>
                  </div>
                  <Field label="Work email" name="email" required>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={update("email")}
                      className={inputCls}
                      placeholder="jane@company.com"
                    />
                  </Field>
                  <Field label="Phone" name="phone">
                    <input
                      data-testid="contact-input-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      className={inputCls}
                      placeholder="+852 ..."
                    />
                  </Field>
                  <Field label="Organisation" name="company">
                    <input
                      data-testid="contact-input-company"
                      value={form.company}
                      onChange={update("company")}
                      className={inputCls}
                      placeholder="Company name"
                    />
                  </Field>
                  <Field label="Enquiry type" name="inquiry_type" required>
                    <select
                      data-testid="contact-input-type"
                      value={form.inquiry_type}
                      onChange={update("inquiry_type")}
                      className={inputCls}
                    >
                      {INQUIRY_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                          {t.label}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="How can we help?" name="message" required>
                      <textarea
                        data-testid="contact-input-message"
                        required
                        rows={6}
                        value={form.message}
                        onChange={update("message")}
                        className={`${inputCls} resize-none`}
                        placeholder="A few lines about your situation, scale and timeline."
                      />
                    </Field>
                  </div>
                  <div className="sm:col-span-2 flex items-center justify-between border-t border-black/10 pt-6 mt-2">
                    <p className="text-xs text-zinc-500 max-w-xs">
                      We respect your privacy. Submissions are handled confidentially and never
                      shared with third parties.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={submitting}
                      className="group inline-flex items-center gap-2 bg-[#E63946] text-white px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#D32F2F] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {submitting ? "Sending…" : (
                        <>
                          Send message
                          <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
